import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Bell, Plus, Trash2, LogIn } from "lucide-react";
import { nexusApi, useNexusAuth } from "@/context/NexusAuthContext";
import "@/pages/nexus/nexus.css";

const CATEGORIES = [
  { id: "", label: "Any category" },
  { id: "regulated_financial_licence", label: "Regulated Financial Licence" },
  { id: "business_stake", label: "Business Stake" },
  { id: "technology_ip", label: "Technology / IP" },
  { id: "fund_structure", label: "Fund Structure" },
  { id: "igaming_platform", label: "iGaming / Platform" },
  { id: "commercial_real_estate", label: "Commercial Real Estate" },
];
const REGIONS = [
  { id: "", label: "Any region" },
  { id: "eu_regulated", label: "EU Regulated" },
  { id: "midshore_offshore", label: "Mid-Shore & Offshore" },
  { id: "north_america", label: "North America" },
  { id: "global", label: "Global" },
];

export default function NexusWatchlist() {
  const { buyer, loading } = useNexusAuth() || {};
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", region_group: "", jurisdiction: "", tags: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    nexusApi.get("/watchlist").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  };

  useEffect(() => {
    if (loading || !buyer) return;
    load();
  }, [buyer, loading]);

  const on = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
    if (!form.category && !form.region_group && !form.jurisdiction.trim() && tags.length === 0) {
      toast.error("Pick at least one filter to save a watchlist");
      return;
    }
    setBusy(true);
    try {
      await nexusApi.post("/watchlist", {
        name: form.name.trim(),
        category: form.category,
        region_group: form.region_group,
        jurisdiction: form.jurisdiction.trim(),
        tags,
      });
      toast.success("Watchlist saved");
      setForm({ name: "", category: "", region_group: "", jurisdiction: "", tags: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save watchlist");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this watchlist?")) return;
    try {
      await nexusApi.delete(`/watchlist/${id}`);
      toast.success("Watchlist removed");
      load();
    } catch { toast.error("Could not delete"); }
  };

  if (loading) {
    return <div className="nx-container py-24 flex items-center justify-center"><Loader2 className="animate-spin text-[#C9896E]" size={28} /></div>;
  }
  if (!buyer) {
    return (
      <div data-testid="nexus-watchlist-anon" className="nx-container py-24 max-w-md mx-auto text-center">
        <LogIn className="mx-auto text-[#C9896E] mb-4" size={30} />
        <h1 className="font-serif text-3xl text-white mb-3">Sign in to save searches</h1>
        <p className="text-white/70 mb-6">Get an instant email the moment a matching new listing goes live.</p>
        <button onClick={() => nav("/nexus/signin?next=/nexus/watchlist")} className="nx-btn-primary" data-testid="watchlist-signin-cta">
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div data-testid="nexus-watchlist" className="nx-container py-16">
      <div className="mb-10">
        <div className="nx-eyebrow rule mb-3">Buyer dashboard</div>
        <h1 className="font-serif text-3xl md:text-4xl text-white">Watchlists & alerts</h1>
        <p className="text-white/60 mt-3 max-w-2xl">
          Save a search — you will get an instant email the moment a matching new listing goes live on Nexus.
        </p>
      </div>

      <form onSubmit={submit} className="border border-white/10 p-6 mb-10 grid gap-4 md:grid-cols-2" data-testid="watchlist-form">
        <div className="md:col-span-2">
          <label className="nx-label">Name this watchlist (optional)</label>
          <input name="name" value={form.name} onChange={on} maxLength={120} placeholder="e.g. EU MiCA CASP structures" className="nx-field" data-testid="watchlist-name" />
        </div>
        <div>
          <label className="nx-label">Category</label>
          <select name="category" value={form.category} onChange={on} className="nx-field" data-testid="watchlist-category">
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="nx-label">Region</label>
          <select name="region_group" value={form.region_group} onChange={on} className="nx-field" data-testid="watchlist-region">
            {REGIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="nx-label">Jurisdiction (optional)</label>
          <input name="jurisdiction" value={form.jurisdiction} onChange={on} maxLength={120} placeholder="e.g. Cyprus, Mauritius" className="nx-field" data-testid="watchlist-jurisdiction" />
        </div>
        <div>
          <label className="nx-label">Tags (comma-separated)</label>
          <input name="tags" value={form.tags} onChange={on} placeholder="e.g. MiCA, CASP" className="nx-field" data-testid="watchlist-tags" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" className="nx-btn-primary" disabled={busy} data-testid="watchlist-save">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <><Plus size={14} /> Save watchlist</>}
          </button>
        </div>
      </form>

      <h2 className="font-serif text-xl text-white mb-4">Your saved watchlists</h2>
      {items === null ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-[#C9896E]" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-white/60" data-testid="watchlist-empty">
          <Bell className="mx-auto text-white/30 mb-4" size={28} />
          You have no active watchlists yet. Save a search above to get instant email alerts.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10" data-testid="watchlist-list">
          {items.map((w) => (
            <div key={w.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`watchlist-row-${w.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-white font-serif text-lg">{w.name || "Untitled watchlist"}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">
                  {[
                    w.category && (CATEGORIES.find((c) => c.id === w.category)?.label || w.category),
                    w.region_group && (REGIONS.find((r) => r.id === w.region_group)?.label || w.region_group),
                    w.jurisdiction,
                    (w.tags || []).length ? w.tags.join(" · ") : null,
                  ].filter(Boolean).join(" · ")}
                </div>
              </div>
              <button onClick={() => del(w.id)} className="p-2 text-white/50 hover:text-red-400" title="Delete" data-testid={`watchlist-delete-${w.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10">
        <Link to="/nexus/listings" className="text-[#C9896E] hover:text-white text-sm font-mono uppercase tracking-[0.18em]">
          → Browse the marketplace
        </Link>
      </div>
    </div>
  );
}
